import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Calendar,
    ChevronRight,
    Home,
    Monitor,
    Search,
    Settings,
} from 'lucide-react';
import AppShell from '../chrome/AppShell';
import {
    Chipbar,
    MenuItem,
    MenuLabel,
    MenuSep,
    Segmented,
    TCard,
    TEmpty,
    TFoot,
    Toolbar,
    TRow,
    TSkeleton,
} from '../components/table';
import {
    Checkbox,
    ConfirmDialog,
    Dialog,
    Field,
    Select,
    TextInput,
    Toggle,
} from '../components/forms';
import { SubNav } from '../components/nav';

// docs/DESIGN-RULES.md rendered with the real tokens and components.
// Reached from the dev toolbar, not the rail.

const SECTIONS = [
    ['one-page', 'Rules'],
    ['color', 'Color'],
    ['bridge', 'Token bridge'],
    ['type', 'Type'],
    ['space', 'Space & radius'],
    ['elevation', 'Elevation'],
    ['motion', 'Motion'],
    ['icons', 'Icons'],
    ['layout', 'Layout'],
    ['components', 'Components'],
    ['libraries', 'Libraries'],
    ['behavior', 'Behavior'],
    ['words', 'Words'],
    ['done', 'Done'],
    ['questions', 'Open questions'],
] as const;

function Sec({
    id,
    n,
    title,
    note,
    children,
}: {
    id: string;
    n: string;
    title: string;
    note?: string;
    children: React.ReactNode;
}) {
    return (
        <section className="sec" id={id}>
            <div className="sec-h">
                <span className="n">{n}</span>
                <h2>{title}</h2>
            </div>
            {note && <p className="rl-note">{note}</p>}
            {children}
        </section>
    );
}

const Row = ({ children }: { children: React.ReactNode }) => (
    <div className="rl-row">{children}</div>
);

const Lab = ({ children }: { children: React.ReactNode }) => (
    <p className="s-lab">{children}</p>
);

function Table({
    head,
    rows,
}: {
    head: string[];
    rows: React.ReactNode[][];
}) {
    return (
        <div className="rl-tablewrap">
            <table className="rl-table">
                <thead>
                    <tr>
                        {head.map((h) => (
                            <th key={h}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {r.map((c, j) => (
                                <td key={j}>{c}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Swatch({
    label,
    color,
    sub,
}: {
    label: string;
    color: string;
    sub?: string;
}) {
    return (
        <div className="rl-sw">
            <div className="chip" style={{ background: color }} />
            <div className="l">{label}</div>
            {sub && <div className="s">{sub}</div>}
        </div>
    );
}

const Code = ({ children }: { children: React.ReactNode }) => (
    <code className="rl-code">{children}</code>
);

/* columns: classic (styles/main.css), v2 (v2/tokens.css), v3 */
const BRIDGE: Array<[string, string, string, string, string]> = [
    ['Page background', '--background', 'hsl(0 0% 100%)', 'hsl(0 0% 100%)', 'var(--chrome)'],
    ['Card / popover', '--card', 'hsl(0 0% 100%)', 'hsl(0 0% 100%)', 'var(--elevated)'],
    ['Primary / text', '--primary', 'hsl(240 5.9% 10%)', 'hsl(220 18% 10%)', 'var(--base)'],
    ['Subtle fill', '--secondary', 'hsl(240 4.8% 95.9%)', 'hsl(220 18% 97%)', 'hsl(0 0% 90.6%)'],
    ['Muted text', '--muted-foreground', 'hsl(240 3.8% 46.1%)', 'hsl(216 9% 42%)', 'var(--text-tertiary)'],
    ['Border', '--border', 'hsl(240 5.9% 90%)', 'hsl(228 15% 94%)', 'hsl(0 0% 87.8%)'],
    ['Focus ring', '--ring', 'hsl(240 5.9% 10%)', 'hsl(243 100% 69%)', 'var(--focus)'],
    ['Destructive', '--destructive', 'hsl(0 84.2% 60.2%)', 'hsl(16 77% 60%)', 'var(--danger)'],
    ['Brand', '--brand (v2)', 'transparent', 'hsl(243 100% 69%)', 'var(--base)'],
];

export default function RulesV3() {
    const [period, setPeriod] = useState<'today' | 'week' | '30d' | 'all'>(
        'week',
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const [check, setCheck] = useState(true);
    const [toggle, setToggle] = useState(false);
    const [chip, setChip] = useState(true);
    const [query, setQuery] = useState('');
    const [done, setDone] = useState<Record<number, boolean>>({});
    const [subTab, setSubTab] = useState('keys');

    useEffect(() => {
        if (!toast) return;
        const t = window.setTimeout(() => setToast(null), 5000);
        return () => window.clearTimeout(t);
    }, [toast]);

    return (
        <AppShell>
            <h1 className="pg-title">
                Design rules <span className="badge">Draft</span>
            </h1>
            <p className="pg-sub">
                docs/DESIGN-RULES.md, rendered with the real tokens and
                components. If something here looks wrong, the rule is
                wrong.
            </p>

            <nav className="rl-index" aria-label="Sections">
                {SECTIONS.map(([id, label]) => (
                    <a key={id} href={`#${id}`}>
                        {label}
                    </a>
                ))}
            </nav>

            <div className="stack rl">
                <Sec id="one-page" n="0" title="The rules on one page">
                    <ol className="rl-list">
                        <li><b>One token source.</b> Every color, size, radius, shadow, and duration is a v3 token.</li>
                        <li><b>One frame.</b> Every dashboard screen renders inside the v3 rail — and the rail stays (icons only) on narrow windows. Sub-pages are a tab row, never a second sidebar.</li>
                        <li><b>One of each component.</b> If v3 has it, use it; if not, build it once and add it to /kit.</li>
                        <li><b>Every screen is an archetype instance.</b> Pick one of the ten.</li>
                        <li><b>Hierarchy by size and tone,</b> not weight or color.</li>
                        <li><b>Visible, not loud.</b> Actionable things are findable at a glance; one dark button per screen.</li>
                        <li><b>Motion is feedback, not decoration.</b></li>
                        <li><b>Behavior rules are design rules</b> (PRD 44–54a).</li>
                        <li><b>One library per job.</b></li>
                        <li><b>A screen is done</b> when it passes the checklist.</li>
                    </ol>
                </Sec>

                <Sec id="color" n="2" title="Color — one source" note="Every neutral is --base at an alpha. Live swatches: these read the actual variables.">
                    <Lab>Primitives</Lab>
                    <Row>
                        <Swatch label="base" color="var(--base)" sub="#141414 ink" />
                        <Swatch label="chrome" color="var(--chrome)" sub="#f5f5f5 page" />
                        <Swatch label="sidebar" color="var(--sidebar)" sub="#f3f3f3 rail" />
                        <Swatch label="elevated" color="var(--elevated)" sub="#fcfcfc cards" />
                        <Swatch label="focus" color="var(--focus)" sub="focus, links" />
                        <Swatch label="live" color="var(--live)" sub="live only" />
                        <Swatch label="good" color="var(--good)" sub="success" />
                        <Swatch label="warn" color="var(--warn)" sub="warning" />
                        <Swatch label="danger" color="var(--danger)" sub="destructive" />
                    </Row>
                    <Lab>Text ramp — on chrome</Lab>
                    <div className="rl-ramp">
                        <span style={{ color: 'var(--text-primary)' }}>primary 100% — titles, body</span>
                        <span style={{ color: 'var(--text-secondary)' }}>secondary 74% — secondary copy</span>
                        <span style={{ color: 'var(--text-tertiary)' }}>tertiary 60% — meta, subtitles, footers (minimum for meta)</span>
                        <span style={{ color: 'var(--text-quaternary)' }}>quaternary 36% — placeholders and disabled only</span>
                    </div>
                    <Lab>Fills and borders</Lab>
                    <Row>
                        {(['primary', 'secondary', 'tertiary', 'quaternary', 'quinary'] as const).map((k) => (
                            <Swatch key={k} label={`bg-${k}`} color={`var(--bg-${k})`} />
                        ))}
                    </Row>
                    <Row>
                        {(['primary', 'secondary', 'tertiary', 'quaternary'] as const).map((k) => (
                            <div key={k} className="rl-sw">
                                <div className="chip" style={{ background: 'var(--elevated)', border: `2px solid var(--border-${k})` }} />
                                <div className="l">border-{k}</div>
                            </div>
                        ))}
                    </Row>
                    <ul className="rl-bullets">
                        <li>No accent color in the chrome — the primary action is a black button.</li>
                        <li>Status colors carry meaning, never decoration. <span className="dot" style={{ marginRight: 4 }} />means capturing within 45 minutes.</li>
                        <li>Brand color is reserved for whitelabel and guest surfaces.</li>
                    </ul>
                </Sec>

                <Sec id="bridge" n="2.3" title="Token bridge — before → after" note="What classic and v2 screens would look like once their variables point at v3. Scaffolding: it goes away with the last classic/v2 screen.">
                    <Table
                        head={['Role', 'Variable', 'Classic', 'v2', 'v3 (bridged)']}
                        rows={BRIDGE.map(([role, v, c, v2, v3]) => [
                            role,
                            <Code>{v}</Code>,
                            <span className="rl-mini" style={{ background: c }} />,
                            <span className="rl-mini" style={{ background: v2 }} />,
                            <span className="rl-mini" style={{ background: v3 }} />,
                        ])}
                    />
                    <p className="rl-note" style={{ marginTop: 10 }}>
                        Radius: classic 3px · v2 10px · <b>v3 6px</b>. Classic's focus ring is near-black and v2's is indigo; both become the one focus blue.
                    </p>
                </Sec>

                <Sec id="type" n="3" title="Type" note="System UI stack. Weight 400 everywhere except the four title roles (600).">
                    <div className="rl-type">
                        <div><span className="spec">20/28 · 600</span><span style={{ fontSize: 20, lineHeight: '28px', fontWeight: 600, letterSpacing: -0.15 }}>Page title</span></div>
                        <div><span className="spec">14/20 · 600</span><span style={{ fontSize: 14, lineHeight: '20px', fontWeight: 600 }}>Dialog title</span></div>
                        <div><span className="spec">13/16 · 600</span><span style={{ fontWeight: 600 }}>Entity name — Acme Summer Gala</span></div>
                        <div><span className="spec">13/18 · 400</span><span>Body, labels, buttons, nav, menu items</span></div>
                        <div><span className="spec">13/18 · 400</span><span style={{ color: 'var(--text-secondary)' }}>Secondary copy</span></div>
                        <div><span className="spec">12/16 · 400</span><span style={{ fontSize: 12, lineHeight: '16px', color: 'var(--text-tertiary)' }}>Meta · Chicago, IL · Friday</span></div>
                        <div><span className="spec">11/14 · 400</span><span style={{ fontSize: 11, lineHeight: '14px', color: 'var(--text-secondary)' }}>Caption, toast subtitle</span></div>
                        <div><span className="spec">9 mono · 600</span><span className="s-lab" style={{ margin: 0 }}>Micro-label</span></div>
                        <div><span className="spec">tabular</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>1,204 · 612 · 38 · 4,111</span></div>
                    </div>
                </Sec>

                <Sec id="space" n="4" title="Space, size, radius">
                    <Lab>4px grid</Lab>
                    <div className="rl-steps">
                        {[4, 6, 8, 12, 16, 24, 32].map((n) => (
                            <div key={n}><span style={{ width: n, height: n }} />{n}</div>
                        ))}
                    </div>
                    <Lab>Control heights</Lab>
                    <Row>
                        <button className="ic" aria-label="24 icon button" title="24 — icon button"><Search size={16} /></button>
                        <button className="tb-btn">28 — button</button>
                        <span className="seg"><button className="on" type="button">28 — segmented</button></span>
                        <label className="tb-search" style={{ maxWidth: 200 }}><Search size={14} /><input placeholder="32 — input" /></label>
                    </Row>
                    <Lab>Radius</Lab>
                    <div className="rl-radii">
                        {[
                            [4, 'badges, chips'],
                            [6, 'buttons, inputs, menus'],
                            [8, 'dialogs, panels'],
                            [12, 'cards, toasts'],
                            [9999, 'avatars, pills'],
                        ].map(([r, use]) => (
                            <div key={r}><span style={{ borderRadius: r as number }} /><b>{r === 9999 ? 'full' : r}</b> {use}</div>
                        ))}
                    </div>
                    <p className="rl-note">Content caps at <b>1040px</b> (under test; the kit's dashboard pages use 864). Editors go full-bleed.</p>
                </Sec>

                <Sec id="elevation" n="5" title="Elevation" note="Hairline first, shadow second. These are the only four.">
                    <div className="rl-elev">
                        <div className="tcard" style={{ padding: 14 }}>Card · 12</div>
                        <div className="menu" style={{ position: 'relative', padding: 14 }}>Menu · 8</div>
                        <div className="dialog" style={{ width: 'auto', padding: 14 }}>Dialog · 8</div>
                        <div className="toast" style={{ width: 'auto' }}><span className="t1">Toast · 12</span></div>
                    </div>
                </Sec>

                <Sec id="motion" n="6" title="Motion — feedback, not decoration" note="150 / 200 / 300ms. Try each allowed example. Reduce Motion in System Settings turns all of them off.">
                    <Table
                        head={['Allowed', 'Try it']}
                        rows={[
                            ['Hover / press color', <Row><button className="tb-btn">Hover me</button><button className="tb-new">Press me</button></Row>],
                            ['Selection slides', <Segmented label="Period" options={[{ value: 'today', label: 'Today' }, { value: 'week', label: 'This week' }, { value: '30d', label: '30 days' }, { value: 'all', label: 'All' }]} value={period} onChange={setPeriod} />],
                            ['Dialog / toast arrives', <Row><button className="tb-btn" onClick={() => setDialogOpen(true)}>Open dialog</button><button className="tb-btn" onClick={() => setToast('Archived “Garden Party Brunch”')}>Show toast</button></Row>],
                            ['Skeleton breathes', <div style={{ width: 280 }}><TCard><TSkeleton rows={2} /></TCard></div>],
                            ['Live dot pulses', <span className="s-live" style={{ margin: 0 }}><span className="dot pulse" /> Live · last capture 4m ago</span>],
                        ]}
                    />
                    <Lab>Not allowed</Lab>
                    <ul className="rl-bullets">
                        <li>Page entrances and staggered list entrances (tested and dropped).</li>
                        <li>Decorative loops, parallax, bounces.</li>
                        <li>Animating a warning in before its data has loaded.</li>
                    </ul>
                </Sec>

                <Sec id="icons" n="7" title="Icons — Lucide only" note="24 grid, stroke 2, currentColor. Secondary at rest, primary on hover.">
                    <div className="rl-icons">
                        {[
                            [12, '28px controls, chips'],
                            [14, 'nav rows, buttons'],
                            [16, '24px icon buttons'],
                            [18, 'toast tiles'],
                        ].map(([size, use]) => (
                            <div key={size}>
                                <span className="set">
                                    <Home size={size as number} />
                                    <Calendar size={size as number} />
                                    <Monitor size={size as number} />
                                    <Settings size={size as number} />
                                    <ChevronRight size={size as number} />
                                </span>
                                <b>{size}px</b> {use}
                            </div>
                        ))}
                    </div>
                    <p className="rl-note">Retire: Heroicons, Radix icons, bootstrap-icons. Icon-only controls always carry a tooltip and aria-label.</p>
                </Sec>

                <Sec id="layout" n="8" title="Layout — one frame, ten archetypes" note="Every dashboard screen renders inside this rail and instantiates one archetype.">
                    <Table
                        head={['#', 'Archetype', 'Exemplar', 'Status', 'Also']}
                        rows={[
                            ['1', 'Launchpad', 'Home', <span className="rl-st">placeholder</span>, '—'],
                            ['2', 'Table / roster', 'Activations', <span className="rl-st ok">native</span>, 'Devices, Staff, Gear, Venues, Contacts, Tasks, People, Shares, admin lists'],
                            ['3', 'Board', 'Design', <span className="rl-st">v2 bridge</span>, '—'],
                            ['4', 'Subject | details', 'Event Overview', <span className="rl-st ok">native</span>, 'Gallery, job/device/venue/staff/contact detail'],
                            ['5', 'Settings form', 'Sharing', <span className="rl-st ok">native</span>, 'Org profile, whitelabel, payments settings'],
                            ['6', 'Media grid', 'Media', <span className="rl-st">v2 bridge</span>, 'Moderation, Favorites, Assets'],
                            ['7', 'Full-screen editor', 'Processing flow', <span className="rl-st">v2 bridge</span>, 'Workflows, JSON editor, HTML templates'],
                            ['8', 'Report', 'Insights', <span className="rl-st">v2 bridge</span>, 'Payments report, org stats, audit log'],
                            ['9', 'Guest surface', 'Public gallery', <span className="rl-st">exempt</span>, 'Tokens only, no frame'],
                            ['10', 'Crew portal', 'My gigs', <span className="rl-st">exempt</span>, 'Tokens only, no frame'],
                        ]}
                    />
                </Sec>

                <Sec id="components" n="9" title="Components — one of each" note="The real kit components. Anything not here gets built once, then added.">
                    <Lab>Secondary navigation — a tab row, never a second sidebar</Lab>
                    <SubNav
                        items={[
                            { id: 'keys', label: 'Keys', href: '#components', count: 5 },
                            { id: 'groups', label: 'Groups', href: '#components', count: 3 },
                            { id: 'auto', label: 'Auto enrollment', href: '#components', count: 2 },
                            { id: 'software', label: 'Software', href: '#components' },
                        ]}
                        active={subTab}
                        onSelect={setSubTab}
                    />
                    <Lab>Buttons</Lab>
                    <Row>
                        <button className="tb-new">Primary</button>
                        <button className="tb-btn">Outline</button>
                        <button className="ic" aria-label="Search" title="Search"><Search size={16} /></button>
                        <button className="tb-new danger">Delete</button>
                        <button className="tb-new" disabled>Disabled</button>
                    </Row>
                    <Lab>Fields</Lab>
                    <div className="rl-fields">
                        <Field label="Name" help="Shown to guests on the gallery.">
                            <TextInput defaultValue="Acme Summer Gala" />
                        </Field>
                        <Field label="Slug" error="That address is taken.">
                            <TextInput defaultValue="acme" aria-invalid="true" />
                        </Field>
                        <Field label="Capture app">
                            <Select defaultValue="clickerbooth">
                                <option value="romr">ROMR</option>
                                <option value="clickerbooth">clickerbooth</option>
                            </Select>
                        </Field>
                    </div>
                    <Row>
                        <Checkbox label="Hold for approval" checked={check} onChange={(e) => setCheck(e.target.checked)} />
                        <Toggle label="Sharing enabled" checked={toggle} onChange={(e) => setToggle(e.target.checked)} />
                        <span className="badge">Archived</span>
                    </Row>
                    <Lab>Menu</Lab>
                    <div className="menu" style={{ position: 'relative', width: 200 }}>
                        <MenuLabel>Sort by</MenuLabel>
                        <MenuItem on onClick={() => {}}>Date</MenuItem>
                        <MenuItem onClick={() => {}}>Name</MenuItem>
                        <MenuSep />
                        <MenuItem onClick={() => {}}>Archive</MenuItem>
                    </div>
                    <Lab>Dialogs and toast</Lab>
                    <Row>
                        <button className="tb-btn" onClick={() => setDialogOpen(true)}>Dialog</button>
                        <button className="tb-btn" onClick={() => setConfirmOpen(true)}>Destructive confirm</button>
                        <button className="tb-btn" onClick={() => setToast('Archived “Garden Party Brunch”')}>Toast with Undo</button>
                    </Row>
                    <Lab>Table page</Lab>
                    <Toolbar
                        searchValue={query}
                        onSearch={setQuery}
                        searchPlaceholder="Search"
                        filterCount={chip ? 1 : 0}
                        primaryLabel="New"
                        onPrimary={() => {}}
                        inline={<Segmented label="Period" options={[{ value: 'week', label: 'This week' }, { value: 'all', label: 'All' }]} value={period === 'all' ? 'all' : 'week'} onChange={(v) => setPeriod(v)} />}
                        menu={(close) => (
                            <MenuItem onClick={close}>Location</MenuItem>
                        )}
                    />
                    <Chipbar chips={chip ? [{ label: 'Location', value: 'Chicago, IL', onClear: () => setChip(false) }] : []} />
                    <TCard>
                        <TRow name="Acme Summer Gala" meta="Chicago, IL" metaDate="Today" date="Today" status={<span className="live">Live</span>} onOpen={() => {}} href="#components" menuItems={[{ label: 'Open', onClick: () => {} }]} />
                        <TRow name="Spring Fling 2026" badge="Archived" meta="Chicago, IL" metaDate="Apr 28" date="Apr 28" status="Wrapped" onOpen={() => {}} href="#components" menuItems={[{ label: 'Open', onClick: () => {} }]} />
                    </TCard>
                    <TFoot>2 of 12 · This week · 1 filter</TFoot>
                    <Lab>States</Lab>
                    <div className="rl-states">
                        <TCard><TSkeleton rows={2} /></TCard>
                        <TCard><TEmpty message="Couldn’t load activations." actionLabel="Retry" onAction={() => {}} /></TCard>
                        <TCard><TEmpty message="Nothing this week." actionLabel="Show next 30 days" onAction={() => {}} /></TCard>
                    </div>
                    <p className="rl-note">Loading · failed · empty — three different states, never confused. Still to build: tooltip (Radix), date / range picker.</p>
                </Sec>

                <Sec id="libraries" n="10" title="Libraries — one per job">
                    <Table
                        head={['Job', 'Keep', 'Retire (no new code)']}
                        rows={[
                            ['Icons', 'Lucide', 'Heroicons, Radix icons, bootstrap-icons'],
                            ['Forms', 'react-hook-form + zod', 'Formik, yup'],
                            ['Dates', 'date-fns *', 'moment, moment-timezone'],
                            ['Toasts', 'sonner (v3-styled) *', 'Radix toast, shadcn toaster'],
                            ['Primitives', 'Radix', 'Headless UI, Tailwind UI copies'],
                            ['Tables', 'TanStack Table + Virtual', 'react-table v7 types'],
                            ['Styling', 'CSS tokens + Tailwind remapped', 'styled-components, v2 tokens, slate vars'],
                        ]}
                    />
                    <p className="rl-note">* not yet confirmed by Josh.</p>
                </Sec>

                <Sec id="behavior" n="11" title="Behavior (PRD 44–54a)">
                    <ul className="rl-bullets">
                        <li>Empty ≠ failed — a failed load shows Retry, never “No X yet.”</li>
                        <li>No warning flashes — skeleton until data resolves.</li>
                        <li>Hold render for auth; a refresh never ejects a user.</li>
                        <li>Live surfaces poll; one dropped poll never shows an outage.</li>
                        <li>Reversible → Undo. Irreversible → confirm. Never both, never neither.</li>
                        <li>No hover-only actions. Everything navigable is a real link.</li>
                        <li>One control, one semantic — instant toggles and save-button fields never mix.</li>
                    </ul>
                </Sec>

                <Sec id="words" n="12" title="Words">
                    <Table
                        head={['Say', 'Not']}
                        rows={[
                            ['devices', 'booths'],
                            ['Captures', 'Photos (as the metric)'],
                            ['Gallery', 'Galleries (per event)'],
                            ['Retry', 'Reprocess'],
                            ['Clone · Archive · Send test', 'OK · Submit'],
                            ['Couldn’t load activations. Retry', 'Error 500'],
                            ['Today · Friday · Oct 12 · Mar 3 ’25', '2026-10-12T00:00:00Z'],
                        ]}
                    />
                </Sec>

                <Sec id="done" n="13" title="Definition of done — per screen" note="Tick through a screen to test it (not saved).">
                    <div className="rl-done">
                        {[
                            'Renders inside the v3 frame',
                            'Instantiates one archetype',
                            'Only v3 tokens — no hex, no Tailwind grays, no slate/v2 vars',
                            'Only kit components; anything new added to /kit',
                            'Only kept libraries',
                            'Loading → skeleton · failed → Retry · empty → honest',
                            'Works at 375px, touch-reachable',
                            'Keyboard reachable, visible focus',
                            'Icon-only controls have tooltips',
                            'Words follow the rules',
                            'Bridge removed, area in V3_NATIVE',
                        ].map((label, i) => (
                            <Checkbox key={i} label={label} checked={!!done[i]} onChange={(e) => setDone({ ...done, [i]: e.target.checked })} />
                        ))}
                    </div>
                </Sec>

                <Sec id="questions" n="15" title="Open questions for Josh">
                    <ol className="rl-list">
                        <li>Content width: 1040 (tested) vs 864 (kit) vs none?</li>
                        <li>Weight: codify 600 for the four title roles only?</li>
                        <li>Rebuild v3 dialog and menus on Radix?</li>
                        <li>Styling: plain CSS + Tailwind remapped, or pick one?</li>
                        <li>Confirm date-fns and sonner.</li>
                        <li>Trial the token bridge on a shared branch?</li>
                        <li>Whitelabel: tint the dashboard, or guest surfaces only?</li>
                    </ol>
                </Sec>
            </div>

            <Dialog
                open={dialogOpen}
                title="Clone activation"
                onClose={() => setDialogOpen(false)}
                onSubmit={() => setDialogOpen(false)}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setDialogOpen(false)}>Cancel</button>
                        <button type="submit" className="tb-new">Clone</button>
                    </>
                }
            >
                <Field label="Name" help="Keeps the same date and job.">
                    <TextInput autoFocus defaultValue="Acme Summer Gala copy" />
                </Field>
            </Dialog>
            <ConfirmDialog
                open={confirmOpen}
                title="Delete this device key?"
                body="The device stops uploading immediately. This can’t be undone."
                actionLabel="Delete key"
                onConfirm={() => {}}
                onClose={() => setConfirmOpen(false)}
            />
            {toast && (
                <div className="toasthost" role="status" aria-live="polite">
                    <div className="toast">
                        <span className="t1" style={{ flex: 1, minWidth: 0 }}>{toast}</span>
                        <button type="button" className="tb-btn" onClick={() => setToast(null)}>Undo</button>
                    </div>
                </div>
            )}
        </AppShell>
    );
}
